/**
 * 슬라이드 등에서 쓰는 오디오: 최초 1회 네트워크 fetch 후 IndexedDB + 메모리 Blob URL로 재사용.
 * BGM처럼 큰 파일도 동일 — 재생한 트랙만 저장(선제 전체 프리로드는 비용↑).
 * fetch 시 `cache: 'force-cache'`로 브라우저 HTTP 캐시도 활용(IDB 비었을 때·CDN 히트 보완).
 */

const DB_NAME = 'ClassPlannerSlideAudioCache';
const DB_VERSION = 1;
const STORE = 'buffers';

const memoryBlobUrlByKey = new Map<string, string>();
const inflightByKey = new Map<string, Promise<string | null>>();

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet(key: string): Promise<ArrayBuffer | undefined> {
  const db = await idbOpen();
  try {
    return await new Promise<ArrayBuffer | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve(r.result as ArrayBuffer | undefined);
    });
  } finally {
    db.close();
  }
}

async function idbPut(key: string, body: ArrayBuffer): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r = tx.objectStore(STORE).put(body, key);
      r.onerror = () => reject(r.error);
      r.onsuccess = () => resolve();
    });
  } finally {
    db.close();
  }
}

function guessAudioMimeType(sourceUrl: string, contentType: string | null): string {
  const ct = contentType?.split(';')[0]?.trim().toLowerCase();
  if (ct && ct.startsWith('audio/')) return ct;
  const u = sourceUrl.toLowerCase();
  if (u.endsWith('.m4a') || u.endsWith('.aac') || u.includes('.m4a')) return 'audio/mp4';
  if (u.endsWith('.webm')) return 'audio/webm';
  if (u.endsWith('.opus')) return 'audio/opus';
  if (u.endsWith('.ogg') || u.endsWith('.oga')) return 'audio/ogg';
  return 'audio/mpeg';
}

async function fetchAudioArrayBuffer(sourceUrl: string): Promise<{ buffer: ArrayBuffer; contentType: string | null } | null> {
  try {
    const res = await fetch(sourceUrl, {
      method: 'GET',
      cache: 'force-cache',
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const buffer = await res.arrayBuffer();
    if (buffer.byteLength === 0) return null;
    return { buffer, contentType: res.headers.get('content-type') };
  } catch {
    return null;
  }
}

/**
 * @param sourceUrl 실제 요청 URL
 * @param cacheKey 동일 파일 식별용(쿼리 제거 URL 등). 비우면 `sourceUrl` 사용.
 * @returns Blob URL — 호출 측에서 `revokeObjectURL` 하지 말 것(세션 동안 재사용).
 */
export async function getCachedAudioBlobUrl(sourceUrl: string, cacheKey?: string): Promise<string | null> {
  const url = sourceUrl.trim();
  if (!url) return null;
  const key = (cacheKey ?? url).trim() || url;

  const mem = memoryBlobUrlByKey.get(key);
  if (mem) return mem;

  let inflight = inflightByKey.get(key);
  if (!inflight) {
    inflight = (async (): Promise<string | null> => {
      try {
        const cached = await idbGet(key);
        if (cached && cached.byteLength > 0) {
          const mime = guessAudioMimeType(url, null);
          const blobUrl = URL.createObjectURL(new Blob([cached], { type: mime }));
          memoryBlobUrlByKey.set(key, blobUrl);
          return blobUrl;
        }
      } catch {
        /* IDB 미지원·거부 */
      }

      const got = await fetchAudioArrayBuffer(url);
      if (!got) return null;

      const mime = guessAudioMimeType(url, got.contentType);
      try {
        await idbPut(key, got.buffer);
      } catch {
        /* 저장 실패해도 재생은 가능 */
      }
      const blobUrl = URL.createObjectURL(new Blob([got.buffer], { type: mime }));
      memoryBlobUrlByKey.set(key, blobUrl);
      return blobUrl;
    })().finally(() => {
      inflightByKey.delete(key);
    });
    inflightByKey.set(key, inflight);
  }

  return inflight;
}

/** 모달 오픈 등에서 백그라운드 프리로드 */
export function primeCachedAudioBlobUrl(sourceUrl: string, cacheKey?: string): void {
  void getCachedAudioBlobUrl(sourceUrl, cacheKey);
}
